import { createEntityId } from '@/core/EntityId';
import type { FloraInstance } from '@/flora/FloraInstance';
import type { FloraWorld } from '@/flora/FloraWorld';

/**
 * Properties shared across every member of an alias group (spec §4.5). Editing any of these on one
 * member propagates to the whole group so linked plants keep a consistent appearance.
 */
export const SHARED_ALIAS_PROPS = [
  'floraDefinitionId',
  'maturity',
  'fullness',
  'pruningStyle',
  'seasonalOffsetDays',
  'colorVariation',
] as const;

export type SharedAliasProp = (typeof SHARED_ALIAS_PROPS)[number];

/**
 * Return a copy of `target` with the shared appearance props taken from `source`. Independent props
 * (position, rotation, locked, hidden, visualSeed, id, aliasGroupId, …) are preserved from `target`.
 */
export function applySharedAliasProps(target: FloraInstance, source: FloraInstance): FloraInstance {
  const merged: FloraInstance = { ...target };
  for (const prop of SHARED_ALIAS_PROPS) {
    // Each shared prop is a primitive, so a direct assignment keeps members fully independent.
    (merged as Record<SharedAliasProp, FloraInstance[SharedAliasProp]>)[prop] = source[prop];
  }
  return merged;
}

/**
 * Manages alias groups over a {@link FloraWorld}. Members share appearance but keep independent
 * transforms; unlinking a member turns it back into a standalone plant.
 */
export class AliasManager {
  constructor(
    private readonly world: FloraWorld,
    private readonly nextGroupId: () => string = () => createEntityId('alias-'),
  ) {}

  /** Ids of all members of a given alias group. */
  membersOf(groupId: string): string[] {
    return this.world
      .all()
      .filter((inst) => inst.aliasGroupId === groupId)
      .map((inst) => inst.id);
  }

  /**
   * Link the given instances into one alias group, adopting the shared appearance of the first id.
   * Returns the new group id, or undefined when fewer than two valid instances are supplied.
   */
  link(ids: string[]): string | undefined {
    const instances = ids
      .map((id) => this.world.get(id))
      .filter((inst): inst is FloraInstance => inst !== undefined);
    if (instances.length < 2) return undefined;

    const groupId = this.nextGroupId();
    const leader = instances[0];
    if (leader === undefined) return undefined;
    this.world.update({ ...leader, aliasGroupId: groupId });
    for (const member of instances.slice(1)) {
      const merged = applySharedAliasProps(member, leader);
      this.world.update({ ...merged, aliasGroupId: groupId }, { rebuild: true });
    }
    return groupId;
  }

  /** Propagate the shared props of `sourceId` to the rest of its alias group. */
  propagate(sourceId: string): void {
    const source = this.world.get(sourceId);
    if (source?.aliasGroupId === undefined) return;
    for (const member of this.world.all()) {
      if (member.id === sourceId || member.aliasGroupId !== source.aliasGroupId) continue;
      this.world.update(applySharedAliasProps(member, source), { rebuild: true });
    }
  }

  /**
   * Detach an instance from its alias group, turning it into an independent copy. If this leaves the
   * group with a single member, that member is unlinked too.
   */
  unlink(id: string): void {
    const inst = this.world.get(id);
    if (inst?.aliasGroupId === undefined) return;
    const groupId = inst.aliasGroupId;

    const detached: FloraInstance = { ...inst };
    delete detached.aliasGroupId;
    this.world.update(detached);

    const remaining = this.membersOf(groupId);
    if (remaining.length === 1) {
      const soleId = remaining[0];
      const sole = soleId === undefined ? undefined : this.world.get(soleId);
      if (sole !== undefined) {
        const soleDetached: FloraInstance = { ...sole };
        delete soleDetached.aliasGroupId;
        this.world.update(soleDetached);
      }
    }
  }
}
